import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, MenuItem, Alert
} from '@mui/material';
import { updateRelative } from '../../../services/relativeService';
import {
  normalizeEmailAddress,
  normalizePhoneNumber,
  sanitizePhoneNumberInput,
  validateOptionalEmailAddress,
  validatePhoneNumber,
} from '../../../utils/contactValidation';

const EditRelativeDialog = ({ open, onClose, onEdit, initialData, clients }) => {
  const [form, setForm] = useState({ relative_name: '', tel_no: '', email: '', client: '' });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ tel_no: '', email: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({
        relative_name: initialData.relative_name || '',
        tel_no: initialData.tel_no || '',
        email: initialData.email || '',
        client: initialData.client || '',
      });
      setFieldErrors({ tel_no: '', email: '' });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue =
      name === 'tel_no'
        ? sanitizePhoneNumberInput(value)
        : name === 'email'
        ? value.trimStart()
        : value;

    setForm({ ...form, [name]: nextValue });
    if (name === 'tel_no') {
      setFieldErrors((prev) => ({ ...prev, tel_no: nextValue ? validatePhoneNumber(nextValue) : '' }));
    }
    if (name === 'email') {
      setFieldErrors((prev) => ({ ...prev, email: validateOptionalEmailAddress(nextValue) }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    const phoneError = validatePhoneNumber(form.tel_no);
    const emailError = validateOptionalEmailAddress(form.email);
    if (phoneError || emailError) {
      setFieldErrors({ tel_no: phoneError, email: emailError });
      setError(phoneError || emailError);
      setLoading(false);
      return;
    }
    try {
      await updateRelative(initialData.id || initialData.relative_id, {
        ...form,
        tel_no: normalizePhoneNumber(form.tel_no),
        email: normalizeEmailAddress(form.email),
      });
      onEdit();
      onClose();
    } catch (err) {
      const msg = err.response?.data
        ? Object.values(err.response.data).flat().join(', ')
        : 'Failed to update';
      setError(msg);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Relative</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          name="relative_name"
          label="Name"
          value={form.relative_name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          name="tel_no"
          label="Tel No"
          value={form.tel_no}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
          type="tel"
          inputProps={{ inputMode: 'tel', maxLength: 20 }}
          error={Boolean(fieldErrors.tel_no)}
          helperText={fieldErrors.tel_no || 'Use 7 to 15 digits. + is allowed.'}
        />
        <TextField
          name="email"
          label="Email Address"
          type="email"
          value={form.email}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={Boolean(fieldErrors.email)}
          helperText={fieldErrors.email || 'Optional'}
        />
        <TextField
          select
          name="client"
          label="Client"
          value={form.client}
          onChange={handleChange}
          fullWidth
          margin="normal"
          required
        >
          {clients.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.first_name} {c.last_name}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRelativeDialog;
